import { useContext, useEffect, useState, useMemo } from 'react';
import { Button, Icon, Modal, Form } from 'semantic-ui-react';
import AppContext from '../../../context/HeaderContext';
import BasicPaginatedTable from '../../../components/table/BasicPaginatedTable';
import BasicTable from '../../../components/table/BasicTable';
import CenterCell from '../../../components/table/CenterCell';
import Loading from '../../../components/Loading';
import JiraLink from '../../../components/JiraLink';
import styled from 'styled-components';
import { getAPI, deleteAPI, postAPI, putAPI } from '../../../libs/fetchAPI';

const TableContainer = styled.div`
	min-height: 700px;
`;

const CustomButton = styled(Button)`
	width: 100px;
	margin-left: -10px !important;
`;

function ConsecutiveFailures() {
	const { current_project_id, user }        = useContext(AppContext);
	const [ , setLoading ]                    = useState(false);
	const [ saving, setSaving ]               = useState(false);
	const [ results, setResults ]             = useState(false);
	const [ show_alerts, setShowAlerts ]      = useState(false);
	const [ show_add_alert, setShowAddAlert ] = useState(false);
	const [ email, setEmail ]                 = useState();
	const [ alerts, setAlerts ]               = useState([]);

	useEffect(() => {
		setLoading(true);

		Promise.all([
			getAPI(`/reports/failing_tests?project_id=${current_project_id}`),
			getAPI(`/alerts?project_id=${current_project_id}`)
		]).then((response) => {
			setResults(response[0].data);
			setAlerts(response[1].data);
			setLoading(false);
		});
	}, [current_project_id]);

	const columns = useMemo(() => {
		function handleMarkResolved(e) {
			const self = e.target;
			const id   = self.getAttribute(`data-id`);

			self.classList.add("loading");

			putAPI(`/reports/failing_tests/${id}`, {
				resolved : 1
			}).then(() => {
				self.classList.remove("loading");

				setResults(results.filter(result => result.id !== Number(id)));
			});
		}

		return [
			{
				Header   : `Spec File`,
				accessor : `spec_file`,
				Cell     : ({ cell }) => <JiraLink issue_key={cell.value} />
			},
			{
				Header   : `Title`,
				accessor : `title`,
			},
			{
				Header   : `Version`,
				accessor : `version`,
			},
			{
				Header         : ``,
				accessor       : `id`,
				collapse       : true,
				disableFilters : true,
				Cell           : ({ cell }) => {
					const row    = cell.row.values;
					const params = {
						spec_file : row.spec_file,
						version   : row.version || ``,
					};

					const link = `${window.location.origin}/suites/single?${new URLSearchParams(params)}`;

					return (
						<CenterCell>
							<a href={link} target="_blank" rel="noreferrer">
								<Button primary>
									<span>History</span>
									<span>&nbsp;&nbsp;<Icon name="external" /></span>
								</Button>
							</a>

							<Button data-id={cell.value} class="ui button olive" onClick={handleMarkResolved}>Mark Resolved</Button>
						</CenterCell>
					);
				}
			}
		];
	}, [results]);

	function handleCloseAlerts() {
		setShowAlerts(false);
	}

	function handleDeleteAlerts(alert_id) {
		const new_alerts = [];

		for(const alert of alerts) {
			if(Number(alert.id !== Number(alert_id))) {
				continue;
			}

			new_alerts.push(alert);
		}

		setAlerts(new_alerts);

		deleteAPI(`/alerts/${alert_id}`);
	}

	function handleAddAlert() {
		setShowAddAlert(true);
	}

	function handleCloseAlert() {
		setShowAddAlert(false);
	}

	function handleSaveAlert() {
		setSaving(true);
		console.log(email);

		postAPI(`/alerts`, {
			project_id : current_project_id,
			email,
		}).then(({ data }) => {
			setAlerts([
				...alerts,
				data[0],
			]);
			setSaving(false);
			setShowAddAlert(false);
		});
	}

	function handleSetValue(e) {
		setEmail(e.target.value);
	}

	if(!results) {
		return <Loading />
	}

	return (
		<>
			<Alerts
				open={show_alerts}
				handleClose={handleCloseAlerts}
				handleDelete={handleDeleteAlerts}
				handleAddAlert={handleAddAlert}
				alerts={alerts}
			/>

			{!!user.admin &&
				<AddAlert
					open={show_add_alert}
					handleSaveAlert={handleSaveAlert}
					handleClose={handleCloseAlert}
					handleSetValue={handleSetValue}
					saving={saving}
				/>
			}

			<CustomButton positive onClick={() => setShowAlerts(true)}>Alerts</CustomButton>

			<TableContainer>
				<BasicPaginatedTable
					columns={columns}
					data={results}
					default_page_size="15"
				/>
			</TableContainer>
		</>
	);
}

function Alerts({ open, handleClose, handleDelete, handleAddAlert, alerts }) {
	const columns = useMemo(() => [
		{
			Header   : `Email`,
			accessor : `email`,
		},
		{
			Header   : ``,
			accessor : `id`,
			collapse : true,
			Cell     : ({ cell }) => <CenterCell><Button negative onClick={() => handleDelete(cell.value)}>Delete</Button></CenterCell>
		}
	], [handleDelete]);

	return (
		<Modal
			size="large"
			closeIcon
			onClose={handleClose}
			open={open}
		>
			<Modal.Header>
				<Button positive onClick={handleAddAlert}>Add Alert</Button>
			</Modal.Header>

			<Modal.Content>
				<BasicTable
					columns={columns}
					data={alerts}
				/>
			</Modal.Content>

			<Modal.Actions>
				<Button color="red" onClick={handleClose}>Close</Button>
			</Modal.Actions>
		</Modal>
	);
}

function AddAlert({ open, handleSaveAlert, handleClose, handleSetValue, saving }) {
	return (
		<Modal
			size="large"
			closeIcon
			onClose={handleClose}
			open={open}
		>
			<Modal.Header>
				Add Alert
			</Modal.Header>

			<Modal.Content>
				<Form>
					<Form.Field>
						<label>Email</label>
						<input placeholder="Email" name="email" onChange={(e) => handleSetValue(e)} />
					</Form.Field>
				</Form>
			</Modal.Content>

			<Modal.Actions>
				<Button negative onClick={handleClose}>Cancel</Button>
				<Button positive onClick={handleSaveAlert} loading={saving}>Save</Button>
			</Modal.Actions>
		</Modal>
	);
}

export default ConsecutiveFailures;
